'use client';

import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Ambient "how to multi-select" hint shown at the top-centre of the board
 * (screen-space, identical position in 2D and 3D — see `LobbyClient`).
 *
 * Shows only the platform-appropriate modifier — ⌘ on macOS, Ctrl on
 * Windows/Linux — rather than the full ⌘/Ctrl/⇧ set. (Shift still works as
 * an alternative, per `use-lobby-basket`; we just keep the hint to one key.)
 *
 * Visibility is owned by `LobbyClient`: it renders this only until the user
 * has multi-selected once (then the dismissal is persisted to localStorage +
 * the DB by address and the hint never returns). The pill itself just
 * self-tracks the modifier to brighten while it's held.
 */
export function BoardSelectPrompt({ className }: { className?: string }) {
  const [modHeld, setModHeld] = useState(false);

  const isMac = useMemo(() => {
    if (typeof navigator === 'undefined') return true;
    const p = navigator.platform || navigator.userAgent || '';
    return /Mac|iPhone|iPad|iPod/i.test(p);
  }, []);
  const modLabel = isMac ? '⌘' : 'Ctrl';

  useEffect(() => {
    // Light the pill up while ANY accepted select modifier is held (Cmd /
    // Ctrl / Shift) even though we only print one — confirms "yes, this is
    // the right key" regardless of which the user reaches for.
    const isMod = (e: KeyboardEvent) =>
      e.key === 'Meta' || e.key === 'Control' || e.key === 'Shift';
    const down = (e: KeyboardEvent) => {
      if (isMod(e)) setModHeld(true);
    };
    const up = (e: KeyboardEvent) => {
      if (isMod(e)) setModHeld(false);
    };
    const reset = () => setModHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', reset);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', reset);
    };
  }, []);

  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none select-none whitespace-nowrap',
        'flex items-center gap-1.5 rounded-[6px] border px-2.5 py-1',
        'bg-black/70 backdrop-blur-md font-mono text-[10px] tracking-wide',
        'transition-colors duration-200',
        modHeld
          ? 'border-cyan-400/70 text-cyan-50'
          : 'border-cyan-500/25 text-cyan-100/60',
        className,
      )}
    >
      <kbd
        className={cn(
          'rounded-[3px] border px-1.5 py-0.5 text-[10px] leading-none transition-colors duration-200',
          modHeld
            ? 'border-cyan-300/70 bg-cyan-400/15 text-cyan-50'
            : 'border-cyan-400/25 bg-white/5 text-cyan-100/70',
        )}
      >
        {modLabel}
      </kbd>
      <span>+ click or drag to select tiles</span>
    </div>
  );
}
